<template>
    <div class="home-page">
        <!-- ---------- 头部：问候 + 学联考倒计时 ---------- -->
        <header class="home-hero">
            <div class="hero-row">
                <div class="hero-text">
                    <p class="hero-hi">{{ greeting }}，{{ displayName }}</p>
                    <p class="hero-sub">今天也把学业往前推一点</p>
                </div>
                <div class="hero-avatar">{{ avatarText }}</div>
            </div>

            <div class="count-card">
                <div class="count-main">
                    <span class="count-label">距离 · 学联考</span>
                    <span class="count-num"><em>{{ examDays }}</em>天</span>
                </div>
                <div class="count-bar"><i :style="{ width: examPercent + '%' }"></i></div>
                <div class="count-foot">
                    <span>{{ examDate }}</span>
                    <span>{{ examTip }}</span>
                </div>
            </div>
        </header>

        <!-- ---------- 内容 ---------- -->
        <main class="home-body">
            <!-- 学练考总览 -->
            <section class="overview-card">
                <div class="ov-item" v-for="o in overviewItems" :key="o.label">
                    <p class="ov-label">{{ o.label }}</p>
                    <p class="ov-num">
                        {{ o.value }}<em>{{ o.unit }}</em>
                    </p>
                    <p class="ov-sub">{{ o.sub }}</p>
                </div>
            </section>

            <!-- 快捷入口 -->
            <section class="quick-grid">
                <div
                    class="quick-item"
                    v-for="q in shortcuts"
                    :key="q.text"
                    @click="handleGo(q)"
                >
                    <span class="q-ico" v-html="q.icon"></span>
                    <span class="q-txt">{{ q.text }}</span>
                </div>
            </section>

            <!-- 各科学练考 -->
            <section class="block">
                <div class="block-head">
                    <span class="block-title">学练考 · 各科</span>
                    <span class="block-more" v-if="hasMoreSubjects" @click="showAllSubjects = !showAllSubjects">
                        {{ showAllSubjects ? '收起 ⌃' : '全部 ' + subjects.length + ' 科 ›' }}
                    </span>
                </div>

                <div class="legend">
                    <span class="legend-item" v-for="l in legends" :key="l">{{ l }}</span>
                </div>

                <div class="subject-card">
                    <div class="subject-wrap" v-for="s in subjectList" :key="s.key">
                        <div class="subject-row" :class="{ open: activeKey === s.key }" @click="toggleSubject(s.key)">
                            <div class="sj-badge" :style="{ background: s.meta.bg, color: s.meta.fg }">
                                {{ s.meta.short }}
                            </div>
                            <div class="sj-main">
                                <div class="sj-top">
                                    <span class="sj-name">{{ s.name }}</span>
                                    <span class="sj-mastery">掌握 {{ s.mastery }}%</span>
                                </div>
                                <div class="sj-bar">
                                    <i :class="'tone-' + s.masteryTone" :style="{ width: s.mastery + '%' }"></i>
                                </div>
                            </div>
                            <div class="sj-stat">
                                <p>
                                    练 <b>{{ s.practice ? s.practice.rate + '%' : '—' }}</b>
                                </p>
                                <p>
                                    考 <b>{{ s.exam ? s.exam.score : '—' }}</b>
                                    <span v-if="s.exam && s.exam.delta" :class="s.exam.delta > 0 ? 'up' : 'down'">
                                        {{ (s.exam.delta > 0 ? '↑' : '↓') + Math.abs(s.exam.delta) }}
                                    </span>
                                </p>
                            </div>
                        </div>

                        <!-- 展开：学 / 练 / 考 明细 -->
                        <div class="subject-detail" v-if="activeKey === s.key">
                            <div class="sd-head">
                                <span class="sd-name">{{ s.name }}</span>
                                <span class="sd-exam">
                                    {{ s.exam.term }} · {{ s.exam.score }}/{{ s.exam.full }} · 班级第 {{ s.exam.rank }}
                                </span>
                            </div>

                            <div class="sd-metrics">
                                <div class="sd-m">
                                    <p class="sd-label">学</p>
                                    <p class="sd-num">{{ s.knowledge.done }}/{{ s.knowledge.total }}</p>
                                    <p class="sd-sub">知识点</p>
                                </div>
                                <div class="sd-m">
                                    <p class="sd-label">练</p>
                                    <p class="sd-num">{{ s.practice ? s.practice.count + '题' : '—' }}</p>
                                    <p class="sd-sub">{{ s.practice ? '正确率 ' + s.practice.rate + '%' : '暂无练习' }}</p>
                                </div>
                                <div class="sd-m">
                                    <p class="sd-label">考</p>
                                    <p class="sd-num">{{ s.exam.avg }}</p>
                                    <p class="sd-sub">{{ s.exam.trend.length }} 次均分</p>
                                </div>
                            </div>

                            <div class="sd-trend">
                                <p class="sd-trend-title">最近 {{ s.exam.trend.length }} 次考试</p>
                                <div class="sd-bars">
                                    <div class="sd-bar-col" v-for="(v, i) in s.exam.trend" :key="i">
                                        <div
                                            class="sd-bar"
                                            :class="{ on: i === s.exam.trend.length - 1 }"
                                            :style="{ height: barHeight(s.exam.trend, v) + 'px' }"
                                        ></div>
                                        <span class="sd-bar-num">{{ v }}</span>
                                    </div>
                                </div>
                            </div>

                            <div class="sd-foot">
                                <span>{{ s.name }}作业 · 待交 {{ s.homework.pending }} 项</span>
                                <span class="sd-go" @click.stop="goHwk">去作业 ›</span>
                            </div>
                        </div>
                    </div>

                    <p class="subject-empty" v-if="!subjects.length">还没有科目数据</p>
                </div>
            </section>

            <!-- 作业情况 -->
            <section class="block">
                <div class="block-head">
                    <span class="block-title">作业情况</span>
                    <span class="seg">
                        <i :class="{ on: hwkRange === 'today' }" @click="switchRange('today')">今天</i>
                        <i :class="{ on: hwkRange === 'week' }" @click="switchRange('week')">本周</i>
                    </span>
                </div>

                <div class="hwk-stat">
                    <div class="hs-item">
                        <p class="hs-label">待交</p>
                        <p class="hs-num c-amber">{{ hwkStat.todo }}</p>
                    </div>
                    <div class="hs-item">
                        <p class="hs-label">已交</p>
                        <p class="hs-num c-green">{{ hwkStat.done }}</p>
                    </div>
                    <div class="hs-item">
                        <p class="hs-label">逾期</p>
                        <p class="hs-num c-red">{{ hwkStat.overdue }}</p>
                    </div>
                </div>

                <div class="hwk-list">
                    <div class="hwk-item" v-for="it in hwkItems" :key="it.id" @click="openHomework(it)">
                        <div class="hw-badge" :style="{ background: it.meta.bg, color: it.meta.fg }">
                            {{ it.meta.short }}
                        </div>
                        <div class="hw-main">
                            <p class="hw-title">{{ it.title }}</p>
                            <p class="hw-note">{{ it.due }} · {{ it.note }}</p>
                        </div>
                        <span
                            class="hw-status"
                            :style="{ background: it.statusMeta.bg, color: it.statusMeta.fg }"
                        >
                            {{ it.statusMeta.text }}
                        </span>
                    </div>
                    <p class="subject-empty" v-if="!hwkItems.length">这类作业暂时没有</p>
                </div>

                <p class="hwk-all" @click="goHwk">查看全部作业 ›</p>
            </section>

            <p class="home-foot">湖北 · {{ year }} · 简录学业</p>
        </main>

        <p class="toast-tip" v-if="toast">{{ toast }}</p>
    </div>
</template>

<script>
import { fetchStudyOverview, fetchSubjects, fetchHomework, homeworkStats } from '../../utils/studyData';

/* ---------------- 可配置项 ---------------- */

/** 学联考日期：改成你自己的考试日期即可，倒计时会自动算 */
const EXAM_DATE = '2027-06-07';
/** 倒计时进度条的起点（从这天开始算进度），一般填开始备考的日子 */
const EXAM_START = '2026-09-01';
/** 首页默认先显示几科，其余点「全部」展开 */
const SUBJECT_PREVIEW = 4;
/** 首页作业区最多列几条 */
const HOMEWORK_PREVIEW = 4;

const SHORTCUT_ICONS = {
    exam:
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
        '<rect x="4.4" y="3.4" width="15.2" height="17.2" rx="2.4"/><path d="M8.4 8.2h7.2M8.4 12h7.2M8.4 15.8h4.4"/></svg>',
    pen:
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
        '<path d="M16.4 3.8 20.2 7.6 8.6 19.2l-4.6.8.8-4.6Z"/><path d="m14.4 5.8 3.8 3.8"/></svg>',
    chart:
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
        '<path d="M4 4v16h16"/><path d="m7 15.4 3.6-4.2 3 2.6 4.4-5.6"/></svg>',
    wrong:
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
        '<path d="M12 4.2 21 19.6H3Z"/><path d="M12 9.6v4.4"/>' +
        '<circle cx="12" cy="16.8" r="1" fill="currentColor" stroke="none"/></svg>'
};

function daysBetween(from, to) {
    return Math.ceil((to.getTime() - from.getTime()) / 86400000);
}

function signedText(value, unit) {
    if (value > 0) {
        return '比上次 +' + value + (unit || '');
    }
    if (value < 0) {
        return '比上次 ' + value + (unit || '');
    }
    return '与上次持平';
}

export default {
    name: 'AppHome',
    data() {
        return {
            now: new Date(),
            userName: '',
            /* 学练考 */
            overview: null,
            subjects: [],
            showAllSubjects: false,
            activeKey: '',
            legends: ['学 = 知识点掌握', '练 = 练习正确率', '考 = 最近成绩'],
            /* 作业 */
            hwkRange: 'today',
            homework: [],
            /* 提示 */
            toast: '',
            toastTimer: null,
            shortcuts: [
                { text: '考试练习', path: '/examhome', icon: SHORTCUT_ICONS.exam },
                { text: '我的作业', path: '/hwk/home', icon: SHORTCUT_ICONS.pen },
                { text: '成绩曲线', path: '/score', icon: SHORTCUT_ICONS.chart },
                { text: '错题本', path: '/wrong', icon: SHORTCUT_ICONS.wrong }
            ]
        };
    },
    computed: {
        greeting() {
            const h = this.now.getHours();
            if (h < 6) return '夜深了';
            if (h < 11) return '早上好';
            if (h < 13) return '中午好';
            if (h < 18) return '下午好';
            return '晚上好';
        },
        displayName() {
            return this.userName || '同学';
        },
        avatarText() {
            const n = this.userName;
            return n ? n.slice(0, 1) : '学';
        },
        examDays() {
            const d = daysBetween(this.now, new Date(EXAM_DATE.replace(/-/g, '/')));
            return d > 0 ? d : 0;
        },
        examDate() {
            return EXAM_DATE.replace(/-/g, ' / ') + ' 开考';
        },
        examPercent() {
            const total = daysBetween(
                new Date(EXAM_START.replace(/-/g, '/')),
                new Date(EXAM_DATE.replace(/-/g, '/'))
            );
            if (total <= 0) {
                return 100;
            }
            const left = this.examDays;
            return Math.max(4, Math.min(100, Math.round(((total - left) / total) * 100)));
        },
        examTip() {
            const d = this.examDays;
            if (d > 100) return '时间还宽裕，稳住节奏';
            if (d > 30) return '进入冲刺期了';
            if (d > 0) return '最后一段路，别松手';
            return '已经考完了，好好休息';
        },
        year() {
            return this.now.getFullYear();
        },
        overviewItems() {
            const o = this.overview;
            if (!o) {
                return [];
            }
            return [
                {
                    label: '本周学',
                    value: o.weekStudyHours,
                    unit: 'h',
                    sub: signedText(o.weekStudyDelta, 'h').replace('比上次', '比上周')
                },
                {
                    label: '本周练',
                    value: o.weekPracticeCount,
                    unit: '题',
                    sub: '正确率 ' + o.weekPracticeRate + '%'
                },
                { label: '最近考', value: o.latestExamAvg, unit: '分', sub: signedText(o.latestExamDelta) }
            ];
        },
        subjectList() {
            return this.showAllSubjects ? this.subjects : this.subjects.slice(0, SUBJECT_PREVIEW);
        },
        hasMoreSubjects() {
            return this.subjects.length > SUBJECT_PREVIEW;
        },
        hwkStat() {
            return homeworkStats(this.homework);
        },
        hwkItems() {
            return this.homework.slice(0, HOMEWORK_PREVIEW);
        }
    },
    created() {
        this.userName = this.readUserName();
        this.loadOverview();
        this.loadSubjects();
        this.loadHomework(this.hwkRange);
    },
    beforeDestroy() {
        if (this.toastTimer) {
            clearTimeout(this.toastTimer);
        }
    },
    methods: {
        readUserName() {
            try {
                const keys = ['userName', 'uname', 'loginUserName', 'nickname'];
                for (let i = 0; i < keys.length; i++) {
                    const v = window.localStorage.getItem(keys[i]);
                    if (v && v !== 'null' && v !== 'undefined') {
                        return String(v);
                    }
                }
            } catch (e) {
                /* 忽略隐私模式下的读取失败 */
            }
            return '';
        },
        loadOverview() {
            fetchStudyOverview()
                .then(data => {
                    this.overview = data;
                })
                .catch(() => {
                    this.overview = null;
                });
        },
        loadSubjects() {
            fetchSubjects()
                .then(list => {
                    this.subjects = list || [];
                })
                .catch(() => {
                    this.subjects = [];
                });
        },
        loadHomework(range) {
            fetchHomework(range)
                .then(list => {
                    this.homework = list || [];
                })
                .catch(() => {
                    this.homework = [];
                });
        },
        toggleSubject(key) {
            this.activeKey = this.activeKey === key ? '' : key;
        },
        /** 柱高按该科三次成绩的相对高低算，60~100（750 稿像素） */
        barHeight(trend, value) {
            const max = Math.max.apply(null, trend);
            const min = Math.min.apply(null, trend);
            if (max === min) {
                return 80;
            }
            return Math.round(60 + ((value - min) / (max - min)) * 40);
        },
        switchRange(range) {
            if (this.hwkRange === range) {
                return;
            }
            this.hwkRange = range;
            this.loadHomework(range);
        },
        goHwk() {
            this.$router.push({ path: '/hwk/home' });
        },
        openHomework(item) {
            if (!item || !item.id) {
                this.showToast('这条作业数据不完整');
                return;
            }
            this.$router.push({ path: '/hwk/task/' + item.id });
        },
        handleGo(item) {
            if (item.path) {
                this.$router.push({ path: item.path });
                return;
            }
            this.showToast('这个入口还在整理中');
        },
        showToast(text) {
            this.toast = text;
            if (this.toastTimer) {
                clearTimeout(this.toastTimer);
            }
            this.toastTimer = setTimeout(() => {
                this.toast = '';
            }, 1800);
        }
    }
};
</script>

<style lang="less" scoped>
.home-page {
    min-height: 100vh;
    box-sizing: border-box;
    /* 底部给 tabbar（100px）+ 安全区留位 */
    padding-bottom: calc(150px + constant(safe-area-inset-bottom));
    padding-bottom: calc(150px + env(safe-area-inset-bottom));
    background: #f4f6fa;
}

/* ---------------- 头部 ---------------- */
.home-hero {
    position: relative;
    padding: 56px 40px 66px;
    background: var(--blueTheme, #0066b3);
    border-radius: 0 0 44px 44px;
    overflow: hidden;
    color: #fff;

    /* 两处光斑，让纯色头部不那么平 */
    &::before {
        content: '';
        position: absolute;
        top: -160px;
        right: -110px;
        width: 400px;
        height: 400px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.13);
    }
    &::after {
        content: '';
        position: absolute;
        bottom: -190px;
        left: -90px;
        width: 320px;
        height: 320px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.08);
    }
}

.hero-row {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.hero-hi {
    margin: 0;
    font-size: 42px;
    font-weight: 600;
    letter-spacing: 1px;
}

.hero-sub {
    margin: 16px 0 0;
    font-size: 24px;
    opacity: 0.82;
}

.hero-avatar {
    flex: none;
    width: 88px;
    height: 88px;
    margin-left: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 34px;
    font-weight: 600;
    color: #fff;
    background: rgba(255, 255, 255, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.35);
}

/* 倒计时卡 */
.count-card {
    position: relative;
    z-index: 1;
    margin-top: 34px;
    padding: 26px 30px 24px;
    border-radius: 26px;
    background: rgba(255, 255, 255, 0.16);
    border: 1px solid rgba(255, 255, 255, 0.22);
}

.count-main {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
}

.count-label {
    font-size: 24px;
    opacity: 0.86;
    letter-spacing: 1px;
}

.count-num {
    font-size: 26px;
    opacity: 0.95;

    em {
        font-style: normal;
        font-size: 58px;
        font-weight: 700;
        margin-right: 8px;
        letter-spacing: 1px;
    }
}

.count-bar {
    height: 10px;
    margin: 20px 0 14px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.25);
    overflow: hidden;

    i {
        display: block;
        height: 100%;
        border-radius: 999px;
        background: #fff;
        transition: width 0.8s ease;
    }
}

.count-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 22px;
    opacity: 0.82;
}

/* ---------------- 内容区 ---------------- */
.home-body {
    padding: 0 30px;
}

/* 学练考总览（压在头部上） */
.overview-card {
    position: relative;
    z-index: 2;
    display: flex;
    margin-top: -42px;
    padding: 30px 8px;
    background: #fff;
    border-radius: 26px;
    box-shadow: 0 8px 28px rgba(15, 39, 76, 0.08);
}

.ov-item {
    position: relative;
    flex: 1;
    min-width: 0;
    text-align: center;

    & + .ov-item::before {
        content: '';
        position: absolute;
        left: 0;
        top: 6px;
        bottom: 6px;
        width: 1px;
        background: #eef1f6;
    }
}

.ov-label {
    margin: 0;
    font-size: 22px;
    color: #8a94a6;
}

.ov-num {
    margin: 10px 0 0;
    font-size: 44px;
    font-weight: 600;
    line-height: 1.1;
    color: #22283a;

    em {
        margin-left: 4px;
        font-style: normal;
        font-size: 22px;
        font-weight: 400;
        color: #8a94a6;
    }
}

.ov-sub {
    margin: 8px 0 0;
    font-size: 20px;
    color: #a3abba;
}

/* 快捷入口 */
.quick-grid {
    display: flex;
    margin-top: 28px;
    padding: 34px 12px 30px;
    background: #fff;
    border-radius: 26px;
    box-shadow: 0 8px 28px rgba(15, 39, 76, 0.06);
}

.quick-item {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;

    &:active {
        opacity: 0.7;
    }
}

.q-ico {
    width: 88px;
    height: 88px;
    margin-bottom: 16px;
    border-radius: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--blueTheme, #0066b3);
    background: var(--lightBlueTheme, #d4eafa);

    svg {
        display: block;
        width: 46px;
        height: 46px;
    }
}

.q-txt {
    font-size: 24px;
    color: #2b3445;
}

/* ---------------- 通用区块 ---------------- */
.block {
    margin-top: 34px;
}

.block-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 56px;
    padding: 0 8px;
}

.block-title {
    font-size: 30px;
    font-weight: 600;
    color: #22283a;
}

.block-more {
    font-size: 22px;
    color: #98a1b0;

    &:active {
        opacity: 0.7;
    }
}

/* 今天 / 本周 */
.seg {
    display: flex;
    padding: 4px;
    border-radius: 999px;
    background: #e9edf3;

    i {
        padding: 8px 24px;
        border-radius: 999px;
        font-style: normal;
        font-size: 22px;
        color: #6b7686;

        &.on {
            color: #fff;
            background: var(--blueTheme, #0066b3);
        }
    }
}

/* ---------------- 学练考 ---------------- */
.legend {
    display: flex;
    flex-wrap: wrap;
    margin: 4px 0 18px;
}

.legend-item {
    margin: 8px 12px 0 0;
    padding: 6px 18px;
    border-radius: 999px;
    font-size: 20px;
    color: #5f6b7d;
    background: #fff;
    border: 1px solid #e8ecf2;
}

.subject-card {
    padding: 0 30px;
    background: #fff;
    border-radius: 26px;
    box-shadow: 0 8px 28px rgba(15, 39, 76, 0.06);
}

.subject-wrap {
    & + .subject-wrap {
        border-top: 1px solid #f2f4f8;
    }
}

.subject-row {
    display: flex;
    align-items: center;
    padding: 26px 0;

    &:active {
        opacity: 0.85;
    }
    &.open {
        padding-bottom: 18px;
    }
}

.sj-badge {
    flex: none;
    width: 56px;
    height: 56px;
    border-radius: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 26px;
    font-weight: 600;
}

.sj-main {
    flex: 1;
    min-width: 0;
    margin-left: 22px;
}

.sj-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.sj-name {
    font-size: 28px;
    font-weight: 600;
    color: #22283a;
}

.sj-mastery {
    font-size: 22px;
    color: #8a94a6;
}

.sj-bar {
    height: 8px;
    margin-top: 12px;
    border-radius: 999px;
    background: #eef1f6;
    overflow: hidden;

    i {
        display: block;
        height: 100%;
        border-radius: 999px;
        transition: width 0.5s ease;

        &.tone-good {
            background: #1d9e75;
        }
        &.tone-mid {
            background: #378add;
        }
        &.tone-low {
            background: #ef9f27;
        }
        &.tone-none {
            background: #d8dde5;
        }
    }
}

.sj-stat {
    flex: none;
    width: 132px;
    margin-left: 20px;
    text-align: right;
    line-height: 1.5;

    p {
        margin: 0;
        font-size: 22px;
        color: #5f6b7d;
    }

    b {
        font-weight: 600;
        color: #22283a;
    }
    .up {
        color: #085041;
    }
    .down {
        color: #d85a30;
    }
}

/* 展开明细 */
.subject-detail {
    padding: 6px 0 30px;
}

.sd-head {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 24px;
}

.sd-name {
    margin-right: 16px;
    font-size: 26px;
    font-weight: 600;
    color: #22283a;
}

.sd-exam {
    font-size: 22px;
    color: #8a94a6;
}

.sd-metrics {
    display: flex;
    padding: 24px 0;
    border-radius: 20px;
    background: #f7f9fc;
}

.sd-m {
    position: relative;
    flex: 1;
    min-width: 0;
    text-align: center;

    & + .sd-m::before {
        content: '';
        position: absolute;
        left: 0;
        top: 8px;
        bottom: 8px;
        width: 1px;
        background: #e6ebf2;
    }
}

.sd-label {
    margin: 0;
    font-size: 22px;
    color: #8a94a6;
}

.sd-num {
    margin: 10px 0 0;
    font-size: 32px;
    font-weight: 600;
    color: #22283a;
}

.sd-sub {
    margin: 6px 0 0;
    font-size: 20px;
    color: #98a1b0;
}

.sd-trend {
    margin-top: 26px;
    padding-top: 24px;
    border-top: 1px solid #f2f4f8;
}

.sd-trend-title {
    margin: 0 0 18px;
    font-size: 22px;
    color: #8a94a6;
}

.sd-bars {
    display: flex;
    align-items: flex-end;
    padding: 0 10px;
}

.sd-bar-col {
    flex: 1;
    text-align: center;
}

.sd-bar {
    width: 56px;
    margin: 0 auto 12px;
    border-radius: 10px 10px 0 0;
    background: #b5d4f4;

    &.on {
        background: #378add;
    }
}

.sd-bar-num {
    font-size: 22px;
    color: #8a94a6;
}

.sd-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 26px;
    padding-top: 24px;
    border-top: 1px solid #f2f4f8;
    font-size: 22px;
    color: #5f6b7d;
}

.sd-go {
    color: var(--blueTheme, #0066b3);
}

.subject-empty {
    padding: 40px 0;
    text-align: center;
    font-size: 22px;
    color: #a3abba;
}

/* ---------------- 作业 ---------------- */
.hwk-stat {
    display: flex;
    padding: 28px 8px;
    background: #fff;
    border-radius: 26px;
    box-shadow: 0 8px 28px rgba(15, 39, 76, 0.06);
}

.hs-item {
    position: relative;
    flex: 1;
    min-width: 0;
    text-align: center;

    & + .hs-item::before {
        content: '';
        position: absolute;
        left: 0;
        top: 4px;
        bottom: 4px;
        width: 1px;
        background: #eef1f6;
    }
}

.hs-label {
    margin: 0;
    font-size: 22px;
    color: #8a94a6;
}

.hs-num {
    margin: 8px 0 0;
    font-size: 40px;
    font-weight: 600;

    &.c-amber {
        color: #ba7517;
    }
    &.c-green {
        color: #0f6e56;
    }
    &.c-red {
        color: #a32d2d;
    }
}

.hwk-list {
    margin-top: 20px;
    padding: 0 30px;
    background: #fff;
    border-radius: 26px;
    box-shadow: 0 8px 28px rgba(15, 39, 76, 0.06);
}

.hwk-item {
    display: flex;
    align-items: center;
    padding: 26px 0;

    & + .hwk-item {
        border-top: 1px solid #f2f4f8;
    }
    &:active {
        opacity: 0.85;
    }
}

.hw-badge {
    flex: none;
    width: 56px;
    height: 56px;
    border-radius: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 26px;
    font-weight: 600;
}

.hw-main {
    flex: 1;
    min-width: 0;
    margin-left: 22px;
}

.hw-title {
    margin: 0;
    font-size: 28px;
    color: #2b3445;
}

.hw-note {
    margin: 8px 0 0;
    font-size: 22px;
    color: #8a94a6;
}

.hw-status {
    flex: none;
    margin-left: 16px;
    padding: 6px 18px;
    border-radius: 999px;
    font-size: 20px;
}

.hwk-all {
    margin: 26px 0 0;
    text-align: center;
    font-size: 22px;
    color: #98a1b0;

    &:active {
        opacity: 0.7;
    }
}

.home-foot {
    margin: 44px 0 0;
    text-align: center;
    font-size: 20px;
    color: #c2c9d6;
}

/* ---------------- 轻提示 ---------------- */
.toast-tip {
    position: fixed;
    left: 50%;
    bottom: calc(200px + constant(safe-area-inset-bottom));
    bottom: calc(200px + env(safe-area-inset-bottom));
    z-index: 600;
    transform: translateX(-50%);
    padding: 20px 36px;
    border-radius: 999px;
    font-size: 24px;
    color: #fff;
    background: rgba(17, 24, 39, 0.86);
    white-space: nowrap;
}
</style>
